import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Shield, ShieldCheck, ShieldOff, Key, Copy, Check, AlertTriangle, Smartphone } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TwoFactorAuth = ({ onUpdate }) => {
  const { t } = useTranslation();
  const [status, setStatus] = useState({ enabled: false, loading: true });
  const [setupMode, setSetupMode] = useState(false);
  const [disableMode, setDisableMode] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus({ enabled: response.data.enabled, loading: false });
    } catch (err) {
      setStatus({ enabled: false, loading: false });
    }
  };

  const startSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/auth/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetupData(response.data);
      setSetupMode(true);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Failed to start 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const verifySetup = async () => {
    if (verifyCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/auth/2fa/verify-setup`, 
        { code: verifyCode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setBackupCodes(response.data.backup_codes);
      setSuccess('Two-factor authentication has been enabled!');
      setStatus({ enabled: true, loading: false });
      setSetupMode(false);
      setVerifyCode('');
      if (onUpdate) onUpdate();
    } catch (err) {
      const d2 = err.response?.data?.detail;
      setError(typeof d2 === 'string' ? d2 : 'Invalid code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const disable2FA = async () => {
    if (verifyCode.length < 6) {
      setError('Please enter your 2FA code or backup code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/auth/2fa/disable`, 
        { code: verifyCode },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      setSuccess('Two-factor authentication has been disabled');
      setStatus({ enabled: false, loading: false });
      setDisableMode(false);
      setVerifyCode('');
      setBackupCodes(null);
      if (onUpdate) onUpdate();
    } catch (err) {
      const d3 = err.response?.data?.detail;
      setError(typeof d3 === 'string' ? d3 : 'Invalid code');
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = () => {
    if (backupCodes) {
      navigator.clipboard.writeText(backupCodes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const cancelSetup = () => {
    setSetupMode(false);
    setDisableMode(false);
    setSetupData(null);
    setVerifyCode('');
    setError('');
    setBackupCodes(null);
  };

  if (status.loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="h-20 bg-gray-100 dark:bg-gray-700 rounded-xl"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
          status.enabled ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-100 dark:bg-gray-700'
        }`}>
          {status.enabled ? (
            <ShieldCheck size={20} className="text-green-600 dark:text-green-400" />
          ) : (
            <Shield size={20} className="text-gray-500 dark:text-gray-400" />
          )}
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">Two-Factor Authentication</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {status.enabled ? 'Your account is protected with 2FA' : 'Add an extra layer of security'}
          </p>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {success && !backupCodes && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded-xl flex items-center gap-2 text-green-600 dark:text-green-400 text-sm">
          <Check size={16} />
          {success}
        </div>
      )}

      {/* Backup Codes Display */}
      {backupCodes && (
        <div className="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
          <div className="flex items-start gap-3 mb-3">
            <Key size={20} className="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-yellow-800 dark:text-yellow-200">Save Your Backup Codes</h4>
              <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                These codes can be used to access your account if you lose your authenticator device. Each code can only be used once.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 my-4">
            {backupCodes.map((code, i) => (
              <div key={i} className="font-mono text-sm bg-white dark:bg-gray-800 px-3 py-2 rounded border border-yellow-200 dark:border-yellow-700 text-center">
                {code}
              </div>
            ))}
          </div>
          <button
            onClick={copyBackupCodes}
            className="w-full py-2 px-4 bg-yellow-100 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-100 rounded-lg font-medium text-sm flex items-center justify-center gap-2 hover:bg-yellow-200 dark:hover:bg-yellow-700 transition-colors"
          >
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copied!' : 'Copy Backup Codes'}
          </button>
          <button
            onClick={() => setBackupCodes(null)}
            className="w-full mt-2 py-2 text-sm text-yellow-700 dark:text-yellow-300 hover:underline"
          >
            I've saved my codes
          </button>
        </div>
      )}

      {/* Setup Mode */}
      {setupMode && setupData && (
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </p>
            <div className="inline-block p-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm">
              <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48" />
            </div>
          </div>

          <div className="text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Or enter this code manually:</p>
            <code className="text-sm bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded font-mono">
              {setupData.secret}
            </code>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Enter the 6-digit code from your app
            </label>
            <input
              type="text"
              value={verifyCode}
              onChange={(e) => {
                const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                setVerifyCode(val);
                setError('');
              }}
              placeholder="000000"
              className="w-full px-4 py-3 text-center text-2xl font-mono tracking-widest bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-xl focus:outline-none focus:border-[#FF6600] transition-colors"
              maxLength={6}
              data-testid="2fa-verify-input"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={cancelSetup}
              className="flex-1 py-3 px-4 border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={verifySetup}
              disabled={loading || verifyCode.length !== 6}
              className="flex-1 py-3 px-4 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="2fa-verify-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Verify & Enable'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Disable Mode */}
      {disableMode && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            Enter your current 2FA code or a backup code to disable two-factor authentication.
          </p>

          <div>
            <input
              type="text"
              value={verifyCode}
              onChange={(e) => {
                setVerifyCode(e.target.value.toUpperCase());
                setError('');
              }}
              placeholder="Enter code"
              className="w-full px-4 py-3 text-center text-xl font-mono tracking-widest bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-xl focus:outline-none focus:border-red-500 transition-colors"
              data-testid="2fa-disable-input"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={cancelSetup}
              className="flex-1 py-3 px-4 border-2 border-gray-200 dark:border-gray-700 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={disable2FA}
              disabled={loading || verifyCode.length < 6}
              className="flex-1 py-3 px-4 bg-red-500 text-white rounded-xl font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="2fa-disable-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Disable 2FA'
              )}
            </button>
          </div>
        </div>
      )}

      {/* Default View */}
      {!setupMode && !disableMode && !backupCodes && (
        <div className="space-y-4">
          <div className={`p-4 rounded-xl ${
            status.enabled 
              ? 'bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800' 
              : 'bg-gray-50 dark:bg-gray-800'
          }`}>
            <div className="flex items-center gap-3">
              <Smartphone size={24} className={status.enabled ? 'text-green-600' : 'text-gray-400'} />
              <div>
                <p className={`font-medium ${status.enabled ? 'text-green-800 dark:text-green-200' : 'text-gray-700 dark:text-gray-300'}`}>
                  Authenticator App
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {status.enabled 
                    ? 'Use your authenticator app to generate codes when signing in' 
                    : 'Use apps like Google Authenticator or Authy'}
                </p>
              </div>
            </div>
          </div>

          {status.enabled ? (
            <button
              onClick={() => setDisableMode(true)}
              className="w-full py-3 px-4 border-2 border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-xl font-medium hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center justify-center gap-2"
              data-testid="2fa-disable-start-btn"
            >
              <ShieldOff size={18} />
              Disable Two-Factor Authentication
            </button>
          ) : (
            <button
              onClick={startSetup}
              disabled={loading}
              className="w-full py-3 px-4 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white rounded-xl font-medium hover:shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              data-testid="2fa-enable-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <ShieldCheck size={18} />
                  Enable Two-Factor Authentication
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoFactorAuth;
